import { TokenAmount, Trade, TradeType } from '@stoboxpswap/sdk'
import { Text } from '@pancakeswap/uikit'
import { Field } from 'state/swap/actions'
import { useTranslation } from 'contexts/Localization'
import { useUserSlippageTolerance } from 'state/user/hooks'
import { computeSlippageAdjustedAmounts, computeTradePriceBreakdown } from 'utils/prices'
import { AutoColumn } from 'components/Layout/Column'
import QuestionHelper from 'components/QuestionHelper'
import { RowBetween, RowFixed } from 'components/Layout/Row'
import { PairTokenSwapFeeInfoTypes } from 'state/swap/types'
import { useMemo } from 'react'
import FormattedPriceImpact from './FormattedPriceImpact'
import SwapRoute from './SwapRoute'

function TradeSummary({
  trade,
  allowedSlippage,
  feeInfo,
  isLoading,
  securityTokenSelected = false,
}: {
  trade: Trade
  allowedSlippage: number
  securityTokenSelected?: boolean
  feeInfo?: { [key: string]: number } | null
  isLoading?: boolean
}) {
  const { t } = useTranslation()
  const { priceImpactWithoutFee, realizedLPFee } = computeTradePriceBreakdown(trade, feeInfo)
  const isExactIn = trade.tradeType === TradeType.EXACT_INPUT
  const slippageAdjustedAmounts = computeSlippageAdjustedAmounts(trade, allowedSlippage)

  const liquidityFee = useMemo(() => feeInfo?.liquidityFee ?? null, [feeInfo?.liquidityFee])
  const treasuryFee = useMemo(() => feeInfo?.treasuryFee ?? null, [feeInfo?.treasuryFee])
  const burnFee = useMemo(() => feeInfo?.burnFee ?? null, [feeInfo?.burnFee])

  const feeCurrency = realizedLPFee instanceof TokenAmount ? realizedLPFee : trade.inputAmount

  return (
    <AutoColumn style={{ padding: '0 16px' }}>
      <RowBetween>
        <RowFixed>
          <Text scale="sm" color="textSubtle">
            {isExactIn ? t('Minimum received') : t('Maximum sold')}
          </Text>
          <QuestionHelper
            text={t(
              'Your transaction will revert if there is a large, unfavorable price movement before it is confirmed.',
            )}
            ml="4px"
            placement="top-start"
          />
        </RowFixed>
        <RowFixed>
          <Text scale="sm">
            {isExactIn
              ? `${slippageAdjustedAmounts[Field.OUTPUT]?.toSignificant(4)} ${trade.outputAmount.currency.symbol}` ??
                '-'
              : `${slippageAdjustedAmounts[Field.INPUT]?.toSignificant(4)} ${trade.inputAmount.currency.symbol}` ?? '-'}
          </Text>
        </RowFixed>
      </RowBetween>
      <RowBetween>
        <RowFixed>
          <Text scale="sm" color="textSubtle">
            {t('Price Impact')}
          </Text>
          <QuestionHelper
            text={t('The difference between the market price and estimated price due to trade size.')}
            ml="4px"
            placement="top-start"
          />
        </RowFixed>
        <FormattedPriceImpact priceImpact={priceImpactWithoutFee} />
      </RowBetween>

      <RowBetween>
        <RowFixed>
          <Text scale="sm" color="textSubtle">
            {t('Liquidity Provider Fee')}
          </Text>
          <QuestionHelper
            text={
              <>
                <Text mb="12px">
                  {t('For each trade a %amount% fee is paid', {
                    amount: securityTokenSelected
                      ? '0%'
                      : isLoading
                      ? t('Loading…')
                      : `${feeInfo ? (liquidityFee + treasuryFee + burnFee).toFixed(1) : 0.3}%`,
                  })}
                </Text>
                <Text>
                  -{' '}
                  {t('%amount% to LP token holders', {
                    amount: securityTokenSelected ? '0%' : isLoading ? t('Loading…') : `${liquidityFee ?? 0.15}%`,
                  })}
                </Text>
                <Text>
                  -{' '}
                  {t('%amount% to the Treasury and burn Fee', {
                    amount: '0.15%',
                  })}
                </Text>
                {/* <Text>
                  -{' '}
                  {t('%amount% to the Treasury', {
                    amount: securityTokenSelected ? '0%' : isLoading ? t('Loading…') : `${treasuryFee ?? 0.03}%`,
                  })}
                </Text> */}
                {/* <Text>
                  -{' '}
                  {t('%amount% towards STBU buyback and burn', {
                    amount: securityTokenSelected ? '0%' : isLoading ? t('Loading…') : `${burnFee ?? 0.15}%`,
                  })}
                </Text> */}
              </>
            }
            ml="4px"
            placement="top-start"
          />
        </RowFixed>
        <Text scale="sm">
          {realizedLPFee ? `${realizedLPFee.toSignificant(4)} ${feeCurrency.currency.symbol}` : '-'}
        </Text>
      </RowBetween>
    </AutoColumn>
  )
}

export interface AdvancedSwapDetailsProps {
  trade?: Trade
  securityTokenSelected?: boolean
  tokenSwapFeeInfo?: PairTokenSwapFeeInfoTypes
}

export function AdvancedSwapDetails({ trade, securityTokenSelected, tokenSwapFeeInfo }: AdvancedSwapDetailsProps) {
  const { t } = useTranslation()
  const [allowedSlippage] = useUserSlippageTolerance()

  const showRoute = Boolean(trade && trade.route.path.length > 2)

  return (
    <AutoColumn gap="0px">
      {trade && (
        <>
          <TradeSummary
            trade={trade}
            allowedSlippage={allowedSlippage}
            feeInfo={tokenSwapFeeInfo?.feeInfo}
            isLoading={tokenSwapFeeInfo?.isLoading}
            securityTokenSelected={securityTokenSelected}
          />
          {showRoute && (
            <>
              <RowBetween style={{ padding: '0 16px' }}>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <Text scale="sm" color="textSubtle">
                    {t('Route')}
                  </Text>
                  <QuestionHelper
                    text={t('The commission may be higher than indicated due to double pair')}
                    ml="4px"
                    placement="top-start"
                  />
                </span>
                <SwapRoute trade={trade} />
              </RowBetween>
            </>
          )}
        </>
      )}
    </AutoColumn>
  )
}
